/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  ReferenceArea,
  ReferenceDot,
} from 'recharts';
import {
  generateTicksForChart,
  mergeFootprintsIntoMainCurve,
} from '../utils/chart';
import { useRecoilValue } from 'recoil';
import {
  calculateTrajactoryInputSelector,
  outputSelector,
  unitsState,
} from '../state/state';
import FlameIcon from './flameIcon';
import { useTranslation } from 'react-i18next';
import { MediaType, Obstacle, UnitTypes, Units } from '../types/sharedTypes';
import { Box, Grid, GridItem, Text } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { unitFormatter } from '../utils/units';

type ChartProps = {
  printMode?: boolean;
};

const getStrokeColor = ({
  didCutOffOccur,
  printMode,
  mediaType,
}: {
  didCutOffOccur: boolean;
  printMode: boolean;
  mediaType: MediaType;
}): string => {
  if (didCutOffOccur) {
    return '#ff0000';
  }

  if (printMode) {
    return mediaType === 'water' ? 'url(#curveColorWater)' : '#CCCCCC';
  }

  return mediaType === 'water'
    ? 'url(#curveColorWater)'
    : 'url(#curveColorFoam)';
};

const getInterval = ({ scaleMaxValue }: { scaleMaxValue: number }): number => {
  if (scaleMaxValue > 100) {
    return 2;
  }

  if (scaleMaxValue > 50) {
    return 1;
  }

  return 0;
};

const Chart = ({ printMode }: ChartProps) => {
  const { t } = useTranslation();
  const unit = useRecoilValue(unitsState);
  const inputValues = useRecoilValue(outputSelector);
  const { obstacles, mediaType } = useRecoilValue(
    calculateTrajactoryInputSelector
  );
  const ticks = generateTicksForChart(inputValues);

  // Used for displaying result arrows.
  // Gets set with useEffect.
  const [chartHeight, setChartHeight] = useState(0);
  const [arrowWidth, setArrowWidth] = useState(0);
  const [arrowHeight, setArrowHeight] = useState(0);

  const formatTick = (tick: number) => {
    const tickString = tick.toString();
    // If not a full number, return the tick as "hidden".
    if (!Number.isInteger(tick)) return '';
    return tickString;
  };

  const strokeColor = getStrokeColor({
    didCutOffOccur: inputValues?.didCutOffOccur ?? false,
    mediaType,
    printMode: printMode ?? false,
  });

  const bgColor = printMode ? '#ffffff' : 'url(#bgColor)';

  /**
   * Uses last tick values (max. chart values X and Y axis) to cut off obstacles, that are outside of chart range.
   */
  const filterObstacles = (
    item: Obstacle,
    value: 'left' | 'right' | 'bottom' | 'top'
  ) => {
    // Predefine max value for chart space.
    const max = ticks[ticks.length - 1];

    // Workaround for item.top being undefined (roof obstacle)
    const top = item.top ?? max;

    switch (value) {
      case 'left':
        return item.left <= max ? item.left : max;
      case 'right':
        return item.right <= max ? item.right : max;
      case 'bottom':
        return item.bottom <= max ? item.bottom : max;
      case 'top':
        return top <= max ? top : max;
      default:
        return 0;
    }
  };

  // Used for displaying result arrows aside of the chart.
  useEffect(() => {
    const arrowInterval = setInterval(() => {
      const gridRef = document.getElementById('gridRef');

      if (gridRef) {
        // Prepairing variables.
        // @ts-ignore - needed because recharts cant handle reacts ref. have to use document id.
        const newChartHeight = Math.round(gridRef.attributes.height.value);
        // @ts-ignore
        const newChartWidth = Math.round(gridRef.attributes.width.value);
        const newArrowWidth = Math.round(
          (newChartWidth / ticks[ticks.length - 1]) * (inputValues?.range ?? 1)
        );
        const newArrowHeight = Math.round(
          (newChartHeight / ticks[ticks.length - 1]) *
            (inputValues?.highestPoint.y ?? 1)
        );

        // Check if values changed. Reduce state setters.
        if (
          newChartHeight !== chartHeight ||
          newArrowWidth !== arrowWidth ||
          newArrowHeight !== arrowHeight
        ) {
          // New values? Set them!
          setChartHeight(newChartHeight);
          setArrowWidth(newArrowWidth);
          setArrowHeight(newArrowHeight);
        }
      }
    }, 100);

    return () => {
      clearInterval(arrowInterval);
    };
  }, [
    inputValues,
    document.getElementById('gridRef'),
    chartHeight,
    arrowWidth,
    arrowHeight,
  ]);

  /**
   * Used for Y Label (Height in meters) custom placement.
   */
  const CustomYLabel = (props: any) => {
    return (
      <text
        x={-(chartHeight / 2) - props.offset}
        y={props.viewBox.y}
        transform="rotate(-90)"
        textAnchor="middle"
        style={{
          fontFamily: "'Proxima Nova', sans-serif",
        }}
      >
        {t(
          unit === Units.Metric
            ? 'components.chart.heightInMeters'
            : 'components.chart.heightInFoot'
        )}
      </text>
    );
  };

  /**
   * Auto-shrinks tick labels, if getting too long.
   * Otherwise would overlap on mobile views.
   */
  const CustomizedXAxisTick = (props: any) => {
    const { x, y, payload } = props;
    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={0} dy={8} textAnchor="middle" fontSize={12}>
          {unitFormatter(payload.value, UnitTypes.Meter, unit, true)}
        </text>
      </g>
    );
  };
  const CustomizedYAxisTick = (props: any) => {
    const { x, y, payload } = props;
    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={0} dy={3} dx={-8} textAnchor="middle" fontSize={12}>
          {unitFormatter(payload.value, UnitTypes.Meter, unit, true)}
        </text>
      </g>
    );
  };

  return (
    <Box>
      {printMode || (
        <Box
          position="relative"
          left="60px"
          width={arrowWidth}
          textAlign="center"
          fontWeight="bold"
          mb="8"
          display={{ base: 'none', lg: 'block' }}
          whiteSpace="nowrap" // prevents breaking the value.
        >
          <Box
            position="absolute"
            left="15px" // because of left arrow
            bottom="0"
            height="2px"
            width={arrowWidth - 30 + 'px'} // 30 = both arrows (15px each)
            minWidth="10px" // Avoid displaying 0px width if monitor is at angle 0deg,
            bg="black"
            borderRadius="full"
            _before={{
              content: '""',
              position: 'absolute',
              left: '-15px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '15px',
              height: '15px',
              bg: 'black',
              clipPath: 'polygon(0 50%, 100% 100%, 100% 0)',
            }}
            _after={{
              content: '""',
              position: 'absolute',
              right: '-15px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '15px',
              height: '15px',
              bg: 'black',
              clipPath: 'polygon(100% 50%, 0 100%, 0 0)',
            }}
          />
          <Text position="relative" top="-1">
            {t('formatting.numberMaxOneDecimalPoint', {
              number: unitFormatter(
                inputValues?.range ?? 0,
                UnitTypes.Meter,
                unit
              ),
            }) +
              ' ' +
              t(unit === Units.Metric ? 'units.meter' : 'units.foot')}
          </Text>
        </Box>
      )}
      <Grid templateColumns={'1fr auto'} gap={printMode ? 0 : 5}>
        <GridItem>
          <ResponsiveContainer width="99%" aspect={1}>
            <LineChart
              data={mergeFootprintsIntoMainCurve(inputValues)}
              margin={{ top: 20, right: 20 }} // Needed for correct display of last tick values (no cutting off numbers)
            >
              <defs>
                <linearGradient
                  id="curveColorWater"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="0%"
                >
                  <stop
                    offset="0%"
                    stopColor={
                      inputValues?.didCutOffOccur ? '#ff0000' : '#4AA4FF'
                    }
                  />
                  <stop
                    offset="100%"
                    stopColor={
                      inputValues?.didCutOffOccur ? '#ff7f7f' : '#afd9ff'
                    }
                  />
                </linearGradient>
                <linearGradient
                  id="curveColorFoam"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="0%"
                >
                  <stop
                    offset="0%"
                    stopColor={inputValues?.didCutOffOccur ? '#ff0000' : '#fff'}
                  />
                  <stop
                    offset="100%"
                    stopColor={inputValues?.didCutOffOccur ? '#ff7f7f' : '#fff'}
                  />
                </linearGradient>
                <linearGradient id="bgColor" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#FFF" />
                  <stop offset="100%" stopColor="#e8e8e8" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeWidth="1" fill={bgColor} id="gridRef" />
              <XAxis
                dataKey="x"
                xAxisId={0}
                type="number"
                tick={CustomizedXAxisTick}
                ticks={ticks}
                tickCount={ticks.length}
                tickFormatter={(tickItem) => formatTick(tickItem)}
                domain={['auto', 'auto']}
                interval={0}
              />
              <XAxis
                dataKey="minFootprintX"
                xAxisId={1}
                type="number"
                tick={false}
                axisLine={false}
                ticks={ticks}
                interval={0}
                label={
                  printMode
                    ? undefined
                    : {
                        value: t(
                          unit === Units.Metric
                            ? 'components.chart.rangeInMeters'
                            : 'components.chart.rangeInFoot'
                        ),
                        position: 'insideCenter',
                      }
                }
                domain={['dataMin', 'dataMax']}
              />
              <XAxis
                dataKey="maxFootprintX"
                xAxisId={2}
                type="number"
                tick={false}
                axisLine={false}
                ticks={ticks}
                interval={0}
                domain={['dataMin', 'dataMax']}
              />
              <YAxis
                ticks={ticks}
                tick={CustomizedYAxisTick}
                interval={0}
                tickFormatter={(tickItem) => formatTick(tickItem)}
                label={printMode ? undefined : <CustomYLabel />}
                domain={['dataMin', 'dataMax']}
              />
              <Line
                xAxisId={0}
                type="natural"
                dot={false}
                dataKey="y"
                stroke={strokeColor}
                strokeWidth="8"
              />
              <Line
                xAxisId={1}
                type="natural"
                dot={false}
                dataKey="minFootprintY"
                stroke={strokeColor}
                strokeWidth="2"
              />
              <Line
                xAxisId={2}
                type="natural"
                dot={false}
                dataKey="maxFootprintY"
                stroke={strokeColor}
                strokeWidth="2"
              />
              {inputValues?.didCutOffOccur && (
                <ReferenceDot
                  x={inputValues.range}
                  y={0}
                  label={<FlameIcon />}
                  fill="none"
                  stroke="none"
                />
              )}
              {obstacles.map((obstacle, index) => (
                <ReferenceArea
                  key={`obstacle-${index}`}
                  x1={filterObstacles(obstacle, 'left')}
                  x2={filterObstacles(obstacle, 'right')}
                  y1={filterObstacles(obstacle, 'bottom')}
                  y2={filterObstacles(obstacle, 'top')}
                  fill="#283237"
                  ifOverflow="extendDomain"
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </GridItem>
        {printMode || (
          <GridItem display={{ base: 'none', lg: 'block' }}>
            <Box
              whiteSpace="nowrap" // prevents breaking the value.
              position="relative"
              top={chartHeight - arrowHeight + 30 + 10 + 'px'} // + 30 = arrows and + 5 fixes space between chart and container of recharts.
              height={arrowHeight - 30 + 'px'} // 30 = both arrows combined
              textAlign="center"
              fontWeight="bold"
              borderRadius="full"
              _before={{
                content: '""',
                position: 'absolute',
                left: '-6px',
                top: '0',
                transform: 'translateY(-50%)',
                width: '15px',
                height: '15px',
                bg: 'black',
                clipPath: 'polygon(50% 0, 0% 100%, 100% 100%)',
              }}
              _after={{
                content: '""',
                position: 'absolute',
                left: '-6px',
                bottom: '-15px',
                transform: 'translateY(-50%)',
                width: '15px',
                height: '15px',
                bg: 'black',
                clipPath: 'polygon(50% 100%, 0 0, 100% 0)',
              }}
            >
              <Box
                position="absolute"
                top="0"
                left="0"
                height="100%"
                width="3px"
                bg="black"
              />
              <Text
                position="relative"
                left="3"
                top="50%"
                transform="translateY(-50%)"
              >
                {t('formatting.numberMaxOneDecimalPoint', {
                  number: unitFormatter(
                    inputValues?.highestPoint.y ?? 0,
                    UnitTypes.Meter,
                    unit
                  ),
                }) +
                  ' ' +
                  t(unit === Units.Metric ? 'units.meter' : 'units.foot')}
              </Text>
            </Box>
          </GridItem>
        )}
      </Grid>
    </Box>
  );
};

export default Chart;
